import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorState } from '@/components/states';

/**
 * Without a boundary, any render exception white-screens the whole SPA. One wraps the
 * shell and one wraps each route group, so a failure in a feature does not take the
 * navigation with it.
 *
 * ## resetKey
 *
 * A caught error is sticky: React keeps rendering the fallback until something clears
 * the state. The shell's boundary is mounted for the whole session, so without this a
 * single crashing page poisoned every page visited afterwards — you would fix nothing,
 * navigate anywhere, and still be looking at "Something went wrong" with a Try again
 * button that re-rendered the NEW route's children into the OLD error state.
 *
 * Passing the pathname as `resetKey` clears the error whenever the route changes, which
 * is the one moment we know the children are about to be different.
 */
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Shown in the message so a bug report can name the area. */
  area?: string;
  /**
   * Clears a caught error when this value changes. Pass the current route.
   * Navigating away from a broken page must not carry its error along.
   */
  resetKey?: string | number;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(previous: Props): void {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Replaced by the real reporter in Phase 15 (monitoring).
    console.error('Unhandled render error', { error, componentStack: info.componentStack });
  }

  private readonly reset = () => this.setState({ error: null });

  override render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      const devHint =
        import.meta.env.DEV && this.state.error?.message
          ? ` (${this.state.error.message})`
          : '';

      return (
        <ErrorState
          title="Something went wrong"
          description={
            this.props.area
              ? `An unexpected error occurred in ${this.props.area}.${devHint}`
              : `An unexpected error occurred.${devHint}`
          }
          onRetry={this.reset}
        />
      );
    }
    return this.props.children;
  }
}
